import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, forwardRef, Injector, ViewChild, ElementRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, FormControl, ReactiveFormsModule, ValidatorFn, AbstractControl, ValidationErrors, Validator, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { startWith, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { TranslocoService } from '@jsverse/transloco';
import { AllCVOptionsFromDB, SkillWithCategory } from '../models/cv-options.model';
import { getSkills } from '../i18n/skills';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { ErrorStateMatcher } from '@angular/material/core';

/**
 * Custom error state matcher for skill selector
 * Shows error when control is invalid and touched
 */
export class SkillErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return !!(control && control.invalid && control.touched);
  }
}

/**
 * Reusable skill selector component with category-based selection
 * Can be used in create-job-offer, edit-job-offer, stepper-form, etc.
 */
@Component({
  selector: 'app-skill-selector',
  templateUrl: './skill-selector.component.html',
  styleUrls: ['./skill-selector.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatAutocompleteModule, MatIconModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SkillSelectorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SkillSelectorComponent),
      multi: true,
    },
  ],
})
export class SkillSelectorComponent implements OnInit, OnChanges, ControlValueAccessor, Validator {
  @Input() allCVOptionsFromDB!: AllCVOptionsFromDB;
  @Input() placeholder: string = 'Skill*';
  @Input() testId: string = 'skill-selector';
  @Input() appearance: 'outline' | 'fill' = 'outline';
  @Input() cssClass: string = '';
  @Input() initialExpandedCategory?: string;
  @Input() styleVariant: 'dashboard' | 'jobs' = 'dashboard'; // Style variant for different apps

  @Output() skillSelected = new EventEmitter<string>();

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  // Internal form control
  skillControl = new FormControl('');

  // Error state matcher
  errorStateMatcher = new SkillErrorStateMatcher();

  // Category management
  skillsByCategory: Map<string, SkillWithCategory[]> = new Map();
  filteredSkillsByCategory: Map<string, SkillWithCategory[]> = new Map();
  expandedCategories: Set<string> = new Set();
  filteredCategories: string[] = [];
  selectedCategory: string | null = null;

  // ControlValueAccessor implementation
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private valueChangesSubscription: any;

  // Translation services - resolved dynamically to avoid injection errors
  private readonly translate?: TranslateService | null = null;
  private readonly transloco?: TranslocoService | null = null;

  // Track missing translations
  private static missingSkills = new Set<string>();
  private static logTimeout: any = null;

  // Cache for translated skills to improve performance
  private translationCache = new Map<string, string>();

  // Cache for skill translations by language to avoid repeated getSkills() calls
  private skillTranslationsCache: any = null;
  private cachedLanguage: string = '';

  // Cache lowercase translations for faster filtering
  private lowercaseTranslationsCache = new Map<string, string>();

  constructor(private readonly injector: Injector) {
    // Try to get translation services without triggering dependency resolution
    // We wrap in try-catch to handle cases where the service's own dependencies don't exist
    try {
      this.translate = this.injector.get(TranslateService, null, { optional: true, skipSelf: false });
    } catch (e) {
      // TranslateService or its dependencies not available
      this.translate = null;
    }

    try {
      this.transloco = this.injector.get(TranslocoService, null, { optional: true, skipSelf: false });
    } catch (e) {
      // TranslocoService or its dependencies not available
      this.transloco = null;
    }

    if (!this.translate && !this.transloco) {
      console.warn('[SkillSelector] Neither TranslateService nor TranslocoService is available');
    }
  }

  ngOnInit(): void {
    this.groupSkillsByCategory();
    this.setupFiltering();
    this.setupValidation();

    // Expand initial category if provided
    if (this.initialExpandedCategory) {
      this.expandedCategories.add(this.initialExpandedCategory);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle category expansion when editing a skill
    if (changes['initialExpandedCategory'] && changes['initialExpandedCategory'].currentValue) {
      this.expandedCategories.add(changes['initialExpandedCategory'].currentValue);
    }

    // Re-setup validation when allCVOptionsFromDB changes
    if (changes['allCVOptionsFromDB'] && changes['allCVOptionsFromDB'].currentValue) {
      this.groupSkillsByCategory();
      this.setupValidation();
      // Clear caches when data changes
      this.translationCache.clear();
      this.skillTranslationsCache = null;
      this.cachedLanguage = '';
      this.lowercaseTranslationsCache.clear();
    }
  }

  /**
   * Groups skills by their category for easier selection
   */
  private groupSkillsByCategory(): void {
    if (!this.allCVOptionsFromDB?.skills) {
      return;
    }

    this.skillsByCategory.clear();

    this.allCVOptionsFromDB.skills.forEach((skill) => {
      const category = skill.category || 'other';
      if (!this.skillsByCategory.has(category)) {
        this.skillsByCategory.set(category, []);
      }
      this.skillsByCategory.get(category)!.push(skill);
    });

    // Initialize filtered skills with all skills
    this.filteredSkillsByCategory = new Map(this.skillsByCategory);

    // Get all categories for filtering
    this.filteredCategories = Array.from(this.skillsByCategory.keys()).sort();
  }

  /**
   * Setup filtering for autocomplete with debouncing for performance
   */
  private setupFiltering(): void {
    // Subscribe to value changes and update filtered results
    // Add debouncing to reduce filtering frequency during typing
    this.skillControl.valueChanges.pipe(
      startWith(''),
      debounceTime(200), // Increased to 200ms for better performance
      distinctUntilChanged() // Only emit when the value actually changes
    ).subscribe((value) => {
      this._filterSkill(value || '');
    });
  }

  /**
   * Setup validation to ensure only valid skills from the list can be selected
   */
  private setupValidation(): void {
    this.skillControl.setValidators([Validators.required, this.skillValidator()]);
    this.skillControl.updateValueAndValidity();
  }

  /**
   * Custom validator that checks if the entered value is a valid skill key
   */
  private skillValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }

      // Don't validate category selections
      if (typeof control.value === 'string' && control.value.startsWith('__CATEGORY__')) {
        return { invalidSkill: true };
      }

      // Check if the value exists in the skills list
      const validSkill = this.allCVOptionsFromDB?.skills?.some(
        skill => skill.skillKey === control.value
      );

      return validSkill ? null : { invalidSkill: true };
    };
  }

  /**
   * Filter skills based on search term (optimized with caching)
   */
  private _filterSkill(value: string | any): void {
    // Handle case where value is an object (selected skill)
    let filterValue = '';
    if (value && typeof value === 'string') {
      filterValue = value.toLowerCase();
    }

    // Return early if data not loaded yet
    if (!this.allCVOptionsFromDB?.skills) {
      return;
    }

    // If no filter, reset to show all categories and KEEP THEM COLLAPSED for performance
    if (!filterValue) {
      // Batch updates to avoid multiple change detections
      this.filteredCategories = Array.from(this.skillsByCategory.keys()).sort();
      this.filteredSkillsByCategory = new Map(this.skillsByCategory);

      // COLLAPSE ALL categories when clearing input for better performance
      // Only show initial category if set
      this.expandedCategories.clear();
      if (this.initialExpandedCategory) {
        this.expandedCategories.add(this.initialExpandedCategory);
      }

      return;
    }

    // Get current language and skill translations (with caching)
    const currentLang = this.getCurrentLang();

    // Cache skill translations to avoid repeated getSkills() calls
    if (this.cachedLanguage !== currentLang || !this.skillTranslationsCache) {
      this.skillTranslationsCache = getSkills(currentLang);
      this.cachedLanguage = currentLang;
      // Clear lowercase cache when language changes
      this.lowercaseTranslationsCache.clear();
    }

    const skillTranslations = this.skillTranslationsCache;

    // Pre-compute lowercase translations for all skills once
    if (this.lowercaseTranslationsCache.size === 0) {
      this.allCVOptionsFromDB.skills.forEach((skill) => {
        const translatedLabel = (skillTranslations.skills as any)[skill.skillKey] || skill.skillKey;
        this.lowercaseTranslationsCache.set(skill.skillKey, translatedLabel.toLowerCase());
      });
    }

    // Filter skills across all categories based on search term
    const filteredSkills = this.allCVOptionsFromDB.skills.filter((skill) => {
      const skillKeyLower = skill.skillKey.toLowerCase();

      // Check if filterValue matches skill key first (fastest check)
      if (skillKeyLower.includes(filterValue)) {
        return true;
      }

      // Check translated label using cached lowercase version
      const translatedLabelLower = this.lowercaseTranslationsCache.get(skill.skillKey);
      if (translatedLabelLower && translatedLabelLower.includes(filterValue)) {
        return true;
      }

      // Check category label (slowest, do last)
      const categoryLabel = (skillTranslations.skillCategories as any)[skill.category] || skill.category;
      return categoryLabel.toLowerCase().includes(filterValue);
    });

    // Group filtered skills by category
    this.filteredSkillsByCategory.clear();
    const newFilteredCategories: string[] = [];

    filteredSkills.forEach((skill) => {
      const category = skill.category || 'other';
      if (!this.filteredSkillsByCategory.has(category)) {
        this.filteredSkillsByCategory.set(category, []);
        newFilteredCategories.push(category);
      }
      this.filteredSkillsByCategory.get(category)!.push(skill);
    });

    // Update filtered categories - sort once at the end
    this.filteredCategories = newFilteredCategories.sort();

    // Auto-expand only categories with results when filtering
    // Use Set operations to minimize change detection triggers
    const categoriesToExpand = new Set(this.filteredCategories);

    // Only update if there are actual changes
    if (this.expandedCategories.size !== categoriesToExpand.size ||
        !Array.from(categoriesToExpand).every(cat => this.expandedCategories.has(cat))) {
      this.expandedCategories = categoriesToExpand;
    }
  }

  /**
   * Get skills for a specific category (respects current filter)
   */
  getSkillsForCategory(category: string): SkillWithCategory[] {
    return this.filteredSkillsByCategory.get(category) || [];
  }

  /**
   * Get display name for a skill
   */
  getSkillDisplayName(skillKey: string): string {
    return this.translateSkill(skillKey);
  }

  /**
   * Translate category key to display name
   */
  getCategoryDisplayName(categoryKey: string): string {
    if (this.translate) {
      return this.translate.instant('skillCategories.' + categoryKey);
    } else if (this.transloco) {
      return this.transloco.translate('skillCategories.' + categoryKey);
    }
    return categoryKey;
  }

  /**
   * Translate skill key to display name (with caching for performance)
   */
  private translateSkill(skillKey: string): string {
    // Check cache first
    if (this.translationCache.has(skillKey)) {
      return this.translationCache.get(skillKey)!;
    }

    let result = skillKey;

    if (this.translate) {
      result = this.translate.instant('skills.' + skillKey);
    } else if (this.transloco) {
      result = this.transloco.translate('skills.' + skillKey);
    }

    // Check if translation is missing (translation returns the key itself)
    if (result === 'skills.' + skillKey) {
      const previousSize = SkillSelectorComponent.missingSkills.size;
      SkillSelectorComponent.missingSkills.add(skillKey);

      // Only schedule logging if this is a NEW missing skill
      if (SkillSelectorComponent.missingSkills.size > previousSize) {
        // Debounce logging to collect all missing skills before logging
        if (SkillSelectorComponent.logTimeout) {
          clearTimeout(SkillSelectorComponent.logTimeout);
        }

        SkillSelectorComponent.logTimeout = setTimeout(() => {
          if (SkillSelectorComponent.missingSkills.size > 0) {
            const missingList = Array.from(SkillSelectorComponent.missingSkills).sort().join(',');
            console.log('%c🔴 MISSING SKILLS:', 'color: red; font-weight: bold; font-size: 14px;');
            console.log('%c' + missingList, 'color: orange; font-size: 12px;');
            console.log('%cCopy above comma-separated list ↑', 'color: gray; font-style: italic;');
          }
        }, 2000); // Wait 2 seconds to collect all missing skills
      }
    }

    // Cache the result
    this.translationCache.set(skillKey, result);
    return result;
  }

  /**
   * Get current language
   */
  private getCurrentLang(): 'sk' | 'en' | 'cz' {
    if (this.translate) {
      return (this.translate.currentLang || 'en') as 'sk' | 'en' | 'cz';
    } else if (this.transloco) {
      return this.transloco.getActiveLang() as 'sk' | 'en' | 'cz';
    }
    return 'en';
  }

  /**
   * Toggle category expansion
   */
  toggleCategory(category: string, event?: MouseEvent): void {
    if (event) {
      // Stop propagation to prevent the click from bubbling up
      event.stopPropagation();
      event.preventDefault();
    }

    if (this.expandedCategories.has(category)) {
      this.expandedCategories.delete(category);
    } else {
      this.expandedCategories.add(category);
    }
  }

  /**
   * Check if category is expanded
   */
  isCategoryExpanded(category: string): boolean {
    return this.expandedCategories.has(category);
  }

  /**
   * Get count of skills in a category
   */
  getSkillCountForCategory(category: string): number {
    return this.filteredSkillsByCategory.get(category)?.length || 0;
  }

  /**
   * Display function for autocomplete
   */
  displaySkillWith = (skillKey: string): string => {
    if (!skillKey) {
      return '';
    }
    // Don't display category selections
    if (skillKey.startsWith('__CATEGORY__')) {
      return '';
    }
    // Translate the skill key for display
    return this.translateSkill(skillKey);
  };

  /**
   * Handle skill selection
   */
  onSkillSelected(skillKey: string): void {
    // Categories are disabled, so this should only fire for actual skills
    if (skillKey && !skillKey.startsWith('__CATEGORY__')) {
      this.onChange(skillKey);
      this.skillControl.markAsTouched();
      this.skillControl.setErrors(null); // Clear errors when valid skill is selected
      this.onTouched();
      this.skillSelected.emit(skillKey);
    }
  }

  /**
   * Handle blur event
   */
  onBlur(): void {
    this.onTouched();
    this.updateInternalControlState();
  }

  /**
   * Handle autocomplete closed event
   */
  onAutocompleteClosed(): void {
    this.onTouched();
    this.updateInternalControlState();
  }

  /**
   * Update internal control state based on current value
   * This ensures Material form field shows correct error state
   */
  private updateInternalControlState(): void {
    this.skillControl.markAsTouched();
    // Run validation on internal control
    this.skillControl.updateValueAndValidity();
  }

  /**
   * Set which category to expand (useful when editing existing skill)
   */
  setSelectedCategory(category: string | null): void {
    this.selectedCategory = category;
    if (category) {
      this.expandedCategories.add(category);
    }
  }

  /**
   * Reset the component
   */
  reset(): void {
    this.skillControl.setValue('');
    this.selectedCategory = null;
    this.expandedCategories.clear();
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (value) {
      this.skillControl.setValue(value, { emitEvent: false });
    } else {
      // Clear the control when value is null/undefined
      this.skillControl.setValue('', { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
    // Unsubscribe from previous subscription to prevent memory leak
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
    }
    this.valueChangesSubscription = this.skillControl.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.skillControl.disable();
    } else {
      this.skillControl.enable();
    }
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    // Check for required (empty value)
    if (!value || value === '') {
      return { required: true };
    }

    // Check our custom validation for non-empty values (invalid skill key)
    if (this.skillControl.errors) {
      return this.skillControl.errors;
    }

    return null;
  }

  registerOnValidatorChange?(fn: () => void): void {
    // This is called by Angular when validators change
    // We can ignore it for now
  }
}
